/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  useSolomonContext,
  SolomonHintType,
  SolomonHintPriority,
  SolomonAction,
} from "@/components/solomon/SolomonContext";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { streamSolomonChat } from "@/lib/solomon-stream";
import type { PlanContextHints } from "@/hooks/useSolomonPlanner";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureGate } from "@/hooks/useTierGate";
import { toast } from "sonner";
import { solomonErrorMessage } from "@/lib/solomon-error";

interface SolomonChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface SolomonChatConversationState {
  storageKey: string | null;
  messages: SolomonChatMessage[];
}

interface SolomonHintConfig {
  id: string;
  message: string;
  context: string;
  link?: {
    label: string;
    href: string;
  };
  /** Action buttons for interactive hints */
  actions?: SolomonAction[];
  /** Type of hint for styling */
  type?: SolomonHintType;
  /** Priority for ordering */
  priority?: SolomonHintPriority;
  /** Icon to display */
  icon?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
  /** Condition that must be true for hint to show */
  when: boolean;
}

/**
 * Hook to show a Solomon hint when a condition is met.
 * Hints are automatically dismissed and won't reappear in the same context.
 * 
 * @example
 * useSolomon({
 *   id: "request-unassigned",
 *   message: "This request hasn't been assigned yet. Assigning an owner helps ensure follow-through.",
 *   context: "request-detail",
 *   link: { label: "Learn about assignments", href: "/help/assignments" },
 *   when: !request.assignee_id && request.status === "open"
 * });
 */
export function useSolomon(config: SolomonHintConfig) {
  const { showHint, isDismissed } = useSolomonContext();
  const hasShownRef = useRef(false);

  useEffect(() => {
    // Only show if condition is met, not dismissed, and we haven't already shown this hint
    if (config.when && !isDismissed(config.id) && !hasShownRef.current) {
      showHint({
        id: config.id,
        message: config.message,
        context: config.context,
        link: config.link,
        actions: config.actions,
        type: config.type,
        priority: config.priority,
        icon: config.icon,
        metadata: config.metadata,
      });
      hasShownRef.current = true;
    }

    // Reset the ref if condition becomes false
    if (!config.when) {
      hasShownRef.current = false;
    }
  }, [
    config.when,
    config.id,
    config.message,
    config.context,
    config.link,
    config.actions,
    config.type,
    config.priority,
    config.icon,
    config.metadata,
    showHint,
    isDismissed,
  ]);
}

/**
 * Register page-level focus hints for the Solomon action-engine
 * planner. Pages call this when they have an entity in scope so
 * the planner can resolve "this person" / "this group" without
 * the user typing IDs.
 *
 * Mechanics:
 *   - On mount + whenever the focus shape changes, push the focus
 *     into SolomonContext.
 *   - On unmount, clear the focus context.
 *
 * Pass `null` or `undefined` to opt out conditionally — useful
 * when the focused entity is still loading.
 *
 * @example
 *   useSolomonFocus(
 *     person
 *       ? {
 *           focusedPersonId: person.id,
 *           focusedPersonName: `${person.first_name} ${person.last_name}`.trim(),
 *         }
 *       : null,
 *   );
 */
export function useSolomonFocus(focus: PlanContextHints | null | undefined) {
  const ctx = useSolomonContext();
  // TEMP debug — single-line JSON for grep
  if (typeof process !== "undefined" && process.env?.VITEST) {
    console.log(JSON.stringify({ debug: "useSolomonFocus", keys: Object.keys(ctx ?? {}), setFocusContextType: typeof ctx?.setFocusContext }));
  }
  const { setFocusContext } = ctx;

  // Stabilize via JSON identity so the effect doesn't re-fire on
  // new object literals with identical content. PlanContextHints
  // is a flat record of strings, so JSON.stringify is cheap.
  const focusKey = focus ? JSON.stringify(focus) : null;

  useEffect(() => {
    if (!focus) {
      setFocusContext(null);
      return;
    }
    setFocusContext(focus);
    return () => setFocusContext(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, setFocusContext]);
}

/**
 * Pre-defined Solomon hints for common Forge workflows.
 * Use these with the useSolomon hook.
 */
export const SolomonHints = {
  // ============================================
  // BASIC CONTEXTUAL HINTS
  // ============================================

  unassignedRequest: (when: boolean) => ({
    id: "request-unassigned",
    message: "This request hasn't been assigned yet. Assigning an owner helps ensure follow-through.",
    context: "request-detail",
    type: "hint" as const,
    when,
  }),

  pendingContribution: (when: boolean) => ({
    id: "contribution-pending-review",
    message: "This contribution is pending review. Reviewing helps maintain accurate records.",
    context: "contribution-detail",
    type: "hint" as const,
    when,
  }),

  emptyFundAllocation: (when: boolean) => ({
    id: "contribution-no-allocation",
    message: "This contribution has no fund allocation. Allocating to a fund helps with reporting.",
    context: "contribution-entry",
    link: { label: "About fund allocations", href: "/funds" },
    type: "hint" as const,
    when,
  }),

  firstTimeVisitor: (when: boolean) => ({
    id: "person-first-visit",
    message: "This is marked as a first-time visitor. Consider scheduling a follow-up.",
    context: "person-profile",
    type: "hint" as const,
    when,
  }),

  projectNoTasks: (when: boolean) => ({
    id: "project-no-tasks",
    message: "This project has no tasks yet. Breaking work into tasks helps track progress.",
    context: "project-detail",
    type: "hint" as const,
    when,
  }),

  pastDueTask: (when: boolean) => ({
    id: "task-past-due",
    message: "This task is past its due date. Consider updating the timeline or marking complete.",
    context: "task-detail",
    type: "hint" as const,
    when,
  }),

  pledgeBehind: (when: boolean) => ({
    id: "pledge-behind-schedule",
    message: "This pledge is behind its expected fulfillment. A gentle reminder may help.",
    context: "pledge-detail",
    type: "hint" as const,
    when,
  }),

  eventNoAttendance: (when: boolean) => ({
    id: "event-no-attendance",
    message: "No attendance has been recorded for this event.",
    context: "event-detail",
    type: "hint" as const,
    when,
  }),

  // ============================================
  // WORKFLOW PROMPTS (Post-action suggestions)
  // ============================================

  afterRequestClose: (when: boolean, requestType?: string) => ({
    id: `workflow-request-closed-${requestType || "general"}`,
    message: "Would you like to send a follow-up message or schedule a check-in?",
    context: "request-closed",
    type: "workflow" as const,
    priority: "medium" as const,
    icon: "message-circle",
    actions: [
      { label: "Send message", action: "compose-message", variant: "primary" as const },
      { label: "Schedule follow-up", action: "schedule-followup", variant: "secondary" as const },
      { label: "No thanks", action: "dismiss", variant: "default" as const },
    ],
    metadata: { requestType },
    when,
  }),

  afterContributionEntry: (when: boolean) => ({
    id: "workflow-contribution-entered",
    message: "Contribution recorded. Would you like to send a thank-you note?",
    context: "contribution-entered",
    type: "workflow" as const,
    priority: "low" as const,
    icon: "heart",
    actions: [
      { label: "Send thank-you", action: "send-thanks", variant: "primary" as const },
      { label: "Not now", action: "dismiss", variant: "default" as const },
    ],
    when,
  }),

  afterEventComplete: (when: boolean, eventId?: string) => ({
    id: `workflow-event-complete-${eventId || "general"}`,
    message: "Event completed. Ready to record attendance or send a follow-up?",
    context: "event-complete",
    type: "workflow" as const,
    priority: "medium" as const,
    icon: "users",
    actions: [
      { label: "Record attendance", action: "record-attendance", variant: "primary" as const },
      { label: "Send follow-up", action: "send-followup", variant: "secondary" as const },
      { label: "Skip", action: "dismiss", variant: "default" as const },
    ],
    metadata: { eventId },
    when,
  }),

  afterNewMemberAdded: (when: boolean, memberId?: string) => ({
    id: `workflow-new-member-${memberId || "general"}`,
    message: "New member added. Would you like to send a welcome message or add to a group?",
    context: "member-added",
    type: "workflow" as const,
    priority: "medium" as const,
    icon: "user-plus",
    actions: [
      { label: "Send welcome", action: "send-welcome", variant: "primary" as const },
      { label: "Add to group", action: "add-to-group", variant: "secondary" as const },
      { label: "Later", action: "dismiss", variant: "default" as const },
    ],
    metadata: { memberId },
    when,
  }),

  afterProjectComplete: (when: boolean, projectId?: string) => ({
    id: `workflow-project-complete-${projectId || "general"}`,
    message: "Project marked complete. Would you like to send appreciation to the team?",
    context: "project-complete",
    type: "workflow" as const,
    priority: "low" as const,
    icon: "trophy",
    actions: [
      { label: "Thank the team", action: "thank-team", variant: "primary" as const },
      { label: "Archive project", action: "archive-project", variant: "secondary" as const },
      { label: "Done", action: "dismiss", variant: "default" as const },
    ],
    metadata: { projectId },
    when,
  }),

  // ============================================
  // BATCH ACTION DETECTION
  // ============================================

  batchRequestsAvailable: (when: boolean, count: number, type?: string) => ({
    id: `batch-requests-${type || "similar"}`,
    message: `You have ${count} similar ${type || "open"} requests. Handle them together?`,
    context: "requests-list",
    type: "insight" as const,
    priority: "medium" as const,
    icon: "layers",
    actions: [
      { label: "Batch assign", action: "batch-assign", variant: "primary" as const },
      { label: "Batch close", action: "batch-close", variant: "secondary" as const },
      { label: "View all", action: "view-filtered", variant: "default" as const },
    ],
    metadata: { count, requestType: type },
    when,
  }),

  batchContributionsAvailable: (when: boolean, count: number) => ({
    id: "batch-contributions-pending",
    message: `${count} contributions are pending review. Review them in batch?`,
    context: "contributions-list",
    type: "insight" as const,
    priority: "medium" as const,
    icon: "check-square",
    actions: [
      { label: "Batch review", action: "batch-review", variant: "primary" as const },
      { label: "View pending", action: "view-pending", variant: "default" as const },
    ],
    metadata: { count },
    when,
  }),

  batchMessagesAvailable: (when: boolean, count: number, groupType?: string) => ({
    id: `batch-messages-${groupType || "similar"}`,
    message: `${count} ${groupType || "people"} could receive this message. Send to all?`,
    context: "messaging",
    type: "insight" as const,
    priority: "low" as const,
    icon: "send",
    actions: [
      { label: "Message all", action: "message-batch", variant: "primary" as const },
      { label: "Select individually", action: "select-individual", variant: "default" as const },
    ],
    metadata: { count, groupType },
    when,
  }),

  batchTasksPastDue: (when: boolean, count: number, projectId?: string) => ({
    id: `batch-tasks-past-due-${projectId || "all"}`,
    message: `${count} tasks are past due. Update them together?`,
    context: "tasks-list",
    type: "alert" as const,
    priority: "high" as const,
    icon: "alert-triangle",
    actions: [
      { label: "Extend deadlines", action: "batch-extend", variant: "primary" as const },
      { label: "Reassign tasks", action: "batch-reassign", variant: "secondary" as const },
      { label: "View past due", action: "view-past-due", variant: "default" as const },
    ],
    metadata: { count, projectId },
    when,
  }),

  // ============================================
  // AGGREGATE TREND ALERTS
  // ============================================

  givingTrendAlert: (
    when: boolean,
    changePercent: number,
    direction: "up" | "down",
    period?: string
  ) => ({
    id: `trend-giving-${direction}-${period || "recent"}`,
    message:
      direction === "up"
        ? `Giving is up ${changePercent}% compared to ${period || "last period"}!`
        : `Giving is down ${changePercent}% compared to ${period || "last period"}.`,
    context: "finance-dashboard",
    type: "insight" as const,
    priority: direction === "down" ? ("high" as const) : ("low" as const),
    icon: direction === "up" ? "trending-up" : "trending-down",
    actions: [
      { label: "View report", action: "view-giving-report", variant: "primary" as const },
      {
        label: direction === "down" ? "Send appeal" : "Send thanks",
        action: direction === "down" ? "send-appeal" : "send-thanks",
        variant: "secondary" as const,
      },
    ],
    metadata: { changePercent, direction, period },
    when,
  }),

  attendanceTrendAlert: (
    when: boolean,
    changePercent: number,
    direction: "up" | "down",
    eventType?: string
  ) => ({
    id: `trend-attendance-${direction}-${eventType || "all"}`,
    message:
      direction === "up"
        ? `${eventType || "Event"} attendance is up ${changePercent}%!`
        : `${eventType || "Event"} attendance has dropped ${changePercent}%.`,
    context: "events-dashboard",
    type: "insight" as const,
    priority: direction === "down" ? ("medium" as const) : ("low" as const),
    icon: direction === "up" ? "trending-up" : "trending-down",
    actions: [
      { label: "View trends", action: "view-attendance-trends", variant: "primary" as const },
    ],
    metadata: { changePercent, direction, eventType },
    when,
  }),

  staleContactsAlert: (when: boolean, count: number, daysSince: number) => ({
    id: `trend-stale-contacts-${daysSince}`,
    message: `${count} members haven't been contacted in ${daysSince}+ days.`,
    context: "community-dashboard",
    type: "alert" as const,
    priority: "medium" as const,
    icon: "user-x",
    actions: [
      { label: "View list", action: "view-stale-contacts", variant: "primary" as const },
      { label: "Send outreach", action: "send-outreach", variant: "secondary" as const },
    ],
    metadata: { count, daysSince },
    when,
  }),

  workloadImbalanceAlert: (
    when: boolean,
    highAssigneeName: string,
    highCount: number,
    avgCount: number
  ) => ({
    id: `trend-workload-imbalance-${highAssigneeName}`,
    message: `${highAssigneeName} has ${highCount} open items (avg is ${avgCount}). Consider redistributing.`,
    context: "requests-dashboard",
    type: "alert" as const,
    priority: "medium" as const,
    icon: "scale",
    actions: [
      { label: "Reassign items", action: "rebalance-workload", variant: "primary" as const },
      { label: "View breakdown", action: "view-workload", variant: "default" as const },
    ],
    metadata: { highAssigneeName, highCount, avgCount },
    when,
  }),

  upcomingDeadlinesAlert: (when: boolean, count: number, daysAhead: number) => ({
    id: `trend-upcoming-deadlines-${daysAhead}`,
    message: `${count} pledges/tasks are due within ${daysAhead} days.`,
    context: "dashboard",
    type: "alert" as const,
    priority: daysAhead <= 3 ? ("high" as const) : ("medium" as const),
    icon: "calendar-clock",
    actions: [
      { label: "View upcoming", action: "view-upcoming-deadlines", variant: "primary" as const },
      { label: "Send reminders", action: "send-deadline-reminders", variant: "secondary" as const },
    ],
    metadata: { count, daysAhead },
    when,
  }),

  // ============================================
  // ENGAGEMENT & RE-ENGAGEMENT
  // ============================================

  atRiskMembersAlert: (when: boolean, count: number) => ({
    id: "engagement-at-risk",
    message: `${count} member${count > 1 ? "s" : ""} may need re-engagement outreach.`,
    context: "community-dashboard",
    type: "alert" as const,
    priority: "high" as const,
    icon: "heart-off",
    actions: [
      { label: "View members", action: "view-at-risk", variant: "primary" as const },
      { label: "Start outreach", action: "start-outreach-campaign", variant: "secondary" as const },
    ],
    metadata: { count },
    when,
  }),

  decliningEngagementAlert: (when: boolean, count: number) => ({
    id: "engagement-declining",
    message: `${count} member${count > 1 ? "s" : ""} show declining engagement trends.`,
    context: "community-dashboard",
    type: "insight" as const,
    priority: "medium" as const,
    icon: "trending-down",
    actions: [
      { label: "View trends", action: "view-engagement-trends", variant: "primary" as const },
    ],
    metadata: { count },
    when,
  }),

  // ============================================
  // SMART TIMING SUGGESTIONS
  // ============================================

  patternBasedSuggestion: (
    when: boolean,
    action: string,
    reason: string,
    suggestedAction?: string
  ) => ({
    id: `pattern-${action}`,
    message: reason,
    context: "proactive",
    type: "insight" as const,
    priority: "low" as const,
    icon: "lightbulb",
    actions: suggestedAction
      ? [
          { label: "Do it now", action: suggestedAction, variant: "primary" as const },
          { label: "Remind later", action: "remind-later", variant: "default" as const },
        ]
      : [{ label: "Got it", action: "dismiss", variant: "default" as const }],
    metadata: { patternAction: action },
    when,
  }),

  recurringTaskReminder: (when: boolean, taskName: string, frequency: string) => ({
    id: `recurring-${taskName}`,
    message: `Time for your ${frequency} ${taskName}?`,
    context: "proactive",
    type: "workflow" as const,
    priority: "low" as const,
    icon: "repeat",
    actions: [
      { label: "Start now", action: "start-recurring-task", variant: "primary" as const },
      { label: "Skip this time", action: "skip-recurring", variant: "default" as const },
    ],
    metadata: { taskName, frequency },
    when,
  }),
} as const;

const SOLOMON_CHAT_HISTORY_KEY = "solomon-chat-history";

function solomonChatHistoryStorageKey(
  userId: string | null | undefined,
  organizationId: string | null | undefined,
) {
  if (!userId || !organizationId) return null;
  return `${SOLOMON_CHAT_HISTORY_KEY}:${userId}:${organizationId}`;
}

/**
 * Read persisted chat history from sessionStorage. SSR-safe and
 * resilient to corrupt data — returns an empty array on any failure.
 */
function loadPersistedMessages(storageKey: string | null): SolomonChatMessage[] {
  if (typeof window === "undefined" || !storageKey) return [];
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SolomonChatMessage[]) : [];
  } catch {
    return [];
  }
}

export function useSolomonChat() {
  const location = useLocation();
  const { user } = useAuth();
  const { organizationId } = useOrganization();
  // Solomon AI is a Flourish-tier feature. The chat EF call is gated below so
  // lower tiers get an upgrade nudge instead of a (billable) AI response.
  const { hasFeature: solomonEnabled } = useFeatureGate("solomon_ai");
  const storageKey = solomonChatHistoryStorageKey(user?.id, organizationId);
  const [conversation, setConversation] = useState<SolomonChatConversationState>(() => ({
    storageKey,
    messages: loadPersistedMessages(storageKey),
  }));
  const messages = conversation.storageKey === storageKey ? conversation.messages : [];
  const [isLoading, setIsLoading] = useState(false);
  const messagesRef = useRef(messages);
  const requestIdRef = useRef(0);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Reload the scoped conversation when the authenticated user or active
  // organization changes. Incrementing the request id also prevents any in-flight
  // response from the previous tenant context from appending into the new thread.
  useEffect(() => {
    requestIdRef.current += 1;
    setIsLoading(false);
    setConversation({
      storageKey,
      messages: loadPersistedMessages(storageKey),
    });

    if (typeof window !== "undefined") {
      try {
        // Drop the legacy global key so unscoped history cannot be resurrected
        // after the app has switched to per-user/per-org persistence.
        window.sessionStorage.removeItem(SOLOMON_CHAT_HISTORY_KEY);
      } catch {
        // Ignore — scoped persistence still works without legacy cleanup.
      }
    }
  }, [storageKey]);

  // Persist conversation across panel open/close. The Sheet unmounts
  // its content when closed, so without this the hook state resets on
  // every reopen. sessionStorage keeps it for the browser session, scoped
  // to the current authenticated user and organization.
  useEffect(() => {
    if (typeof window === "undefined" || !conversation.storageKey) return;
    try {
      window.sessionStorage.setItem(
        conversation.storageKey,
        JSON.stringify(conversation.messages),
      );
    } catch {
      // Ignore quota / serialization failures — persistence is best-effort.
    }
  }, [conversation]);

  // Buffered (non-streaming) call — the original behavior, used as the
  // fallback when streaming fails before any text arrives.
  const sendBuffered = useCallback(
    async (nextMessages: SolomonChatMessage[], requestId: number) => {
      const { data, error } = await invokeEdgeFunction("solomon-chat", {
        body: {
          messages: nextMessages,
          context: { page: location.pathname, organizationId },
        },
        policy: "interactive",
      });

      if (requestIdRef.current !== requestId) return;
      if (error) throw error;

      if (data?.content) {
        setConversation((prev) => ({
          ...prev,
          messages: [...prev.messages, { role: "assistant", content: data.content }],
        }));
      } else {
        toast.error("Solomon replied with an empty message.");
      }
    },
    [location.pathname, organizationId],
  );

  const sendMessage = useCallback(
    async (content: string) => {
      if (!solomonEnabled) {
        setConversation((prev) => ({
          ...prev,
          messages: [
            ...prev.messages,
            { role: "user", content },
            {
              role: "assistant",
              content:
                "Ask Solomon AI is available on the Flourish plan. Upgrade in Settings → Subscription to unlock AI assistance.",
            },
          ],
        }));
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const userMessage: SolomonChatMessage = { role: "user", content };
      const nextMessages = [...messagesRef.current, userMessage];
      setConversation({ storageKey, messages: nextMessages });
      setIsLoading(true);

      try {
        // Try streaming first for live, token-by-token replies. Append a
        // single assistant bubble on the first chunk, then grow it.
        let streamedText = "";
        let assistantAdded = false;
        const appendDelta = (text: string) => {
          if (requestIdRef.current !== requestId) return;
          streamedText += text;
          setConversation((prev) => {
            if (!assistantAdded) {
              assistantAdded = true;
              return {
                ...prev,
                messages: [...prev.messages, { role: "assistant", content: streamedText }],
              };
            }
            const copy = prev.messages.slice();
            copy[copy.length - 1] = { role: "assistant", content: streamedText };
            return { ...prev, messages: copy };
          });
        };

        try {
          await streamSolomonChat({
            messages: nextMessages,
            context: { page: location.pathname, organizationId },
            onDelta: appendDelta,
          });
          if (requestIdRef.current !== requestId) return;

          // Stream completed but produced nothing — fall back to buffered.
          if (!streamedText.trim()) {
            await sendBuffered(nextMessages, requestId);
          }
        } catch (streamErr) {
          if (requestIdRef.current !== requestId) return;
          if (assistantAdded && streamedText.trim()) {
            // Partial answer already on screen — keep it, surface the error,
            // and don't fall back (that would duplicate the reply).
            console.error("[solomon-chat] stream interrupted", streamErr);
            toast.error(solomonErrorMessage(streamErr));
          } else {
            // Nothing streamed yet — degrade to the buffered endpoint so a
            // streaming failure is invisible to the user.
            console.warn("[solomon-chat] streaming failed, falling back", streamErr);
            await sendBuffered(nextMessages, requestId);
          }
        }
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        console.error("[solomon-chat] request failed", err);
        toast.error(solomonErrorMessage(err));
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [location.pathname, organizationId, sendBuffered, storageKey, solomonEnabled],
  );

  const clearHistory = useCallback(() => {
    requestIdRef.current += 1;
    setConversation({ storageKey, messages: [] });
    setIsLoading(false);
    if (storageKey && typeof window !== "undefined") {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch {
        // Ignore — clearing in-memory state above is what matters.
      }
    }
  }, [storageKey]);

  return { messages, isLoading, sendMessage, clearHistory };
}
